import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { BookOpen } from 'lucide-react';

/**
 * The console's vocabulary, in one place.
 *
 * Requested at the 2026-08 DSS review: the tabs use words ("session", "turn",
 * "hands-free") whose precise meaning lives in backend docstrings that readers
 * of the console never see. Definitions here are the BLESSED wording — the
 * same sentences the team reviewed — so edit deliberately: each one states
 * what the number means, and two carry caveats that keep the console honest
 * about known measurement limits.
 */

const TERMS = [
  {
    term: 'Session',
    def: 'One conversation — all turns sharing a session ID.',
    caveat: 'On web, the widget currently borrows the storefront’s cache ID, '
      + 'which can restart mid-conversation, so one conversation may appear as '
      + 'two adjacent rows until the widget update ships.',
  },
  {
    term: 'Turn',
    def: 'One user message plus the assistant’s reply.',
  },
  {
    term: 'Hands-free (Voice)',
    def: 'A spoken conversation with the voice assistant — voice in, voice out.',
  },
  {
    term: 'Dictation',
    def: 'The user spoke to draft a message, then sent it as text.',
  },
  {
    term: 'Production View',
    def: 'Default filter. Hides traffic labeled staging/local; keeps unlabeled '
      + 'traffic and discloses its count.',
  },
  {
    term: 'Audience',
    def: 'All / Customers only (hides internal accounts) / Internal only.',
  },
  {
    term: 'Returning User',
    def: 'A user with more than one session in the selected date range.',
  },
];

export default function TermsLegend() {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline">
          <BookOpen className="h-4 w-4 mr-2" />
          Terms
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>What the words on this console mean</DialogTitle>
        </DialogHeader>
        <dl className="space-y-4 text-sm">
          {TERMS.map(({ term, def, caveat }) => (
            <div key={term}>
              <dt className="font-medium">{term}</dt>
              <dd className="text-gray-600 dark:text-gray-400">{def}</dd>
              {caveat && (
                <dd className="mt-1 text-xs italic text-amber-700 dark:text-amber-400">
                  {caveat}
                </dd>
              )}
            </div>
          ))}
        </dl>
      </DialogContent>
    </Dialog>
  );
}
